import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SettingsService } from '../../../services/settings/settings.service';
import { UserSettingsService, ThemeKey } from '../../../services/user-settings/user-settings.service';
import { DeviceSettingsService, ShowNumberpadOverride } from '../../../services/device-settings/device-settings.service';
import { ClientSettingsService } from '../../../services/client-settings/client-settings.service';
import { AppUpdateService } from '../../../services/app-update/app-update.service';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { LocationService } from '../../../services/location/location.service';
import { DeviceAuthService, DeviceAuth } from '../../../services/device-auth/device-auth.service';
import { InventoryCacheService, CacheStatus } from '../../../services/inventory-cache/inventory-cache.service';
import { TransferQueueService, QueueStatus } from '../../../services/transfer-queue/transfer-queue.service';
import { AppComponent } from '../../../app.component';
import { DialogOption } from '../ui/dialog/dialog-options';

const DEVICE_AUTHORIZE_PERMISSION = 'device.authorize';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {

  activeTheme: ThemeKey = 'light';
  themes: { key: ThemeKey, label: string }[] = [
    { key: 'light', label: 'Light' },
    { key: 'dark', label: 'Dark' },
    { key: 'blueIce', label: 'Blue Ice' },
    { key: 'redFire', label: 'Red Fire' },
  ];

  showNumberpadOverride: ShowNumberpadOverride = 'default';
  numberpadOptions: { key: ShowNumberpadOverride, label: string }[] = [
    { key: 'default', label: 'Client Default' },
    { key: 'on', label: 'On' },
    { key: 'off', label: 'Off' },
  ];

  // Device authorization for PIN sign-in
  deviceAuth: DeviceAuth | null = null;
  deviceSession = false;
  deviceAuthEnabled = false;
  deviceAuthMaxDays = 30;
  canAuthorizeDevice = false;
  authorizeLocations: Array<any> = [];
  authorizeOpen = false;
  authorizeName = '';
  authorizeLocationId: string = null;
  authorizeDays = 30;
  authorizing = false;
  removingDevice = false;
  deviceError: string = null;

  version: string;
  // Signed-in user for the "Signed In" block (header hides the name on handhelds).
  user: any = null;
  updateStatus: 'idle' | 'checking' | 'up-to-date' | 'updated' | 'error' = 'idle';
  updateReady = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private settingsService: SettingsService,
    private userSettingsService: UserSettingsService,
    private deviceSettingsService: DeviceSettingsService,
    private clientSettingsService: ClientSettingsService,
    private appUpdateService: AppUpdateService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private locationService: LocationService,
    private deviceAuthService: DeviceAuthService,
    private inventoryCacheService: InventoryCacheService,
    private transferQueueService: TransferQueueService,
  ) { }

  // --- Local Inventory Cache ---------------------------------------------------
  localCacheEnabled = false;
  cacheStatus: CacheStatus | null = null;
  queueStatus: QueueStatus | null = null;

  setLocalCache(enabled: boolean) {
    this.localCacheEnabled = enabled;
    this.deviceSettingsService.setLocalInventoryCache(enabled);
    if (!enabled) {
      this.inventoryCacheService.clear();
    }
  }

  syncCacheNow() {
    this.inventoryCacheService.sync();
    this.transferQueueService.scheduleReplay(0);
  }

  async reloadCache() {
    await this.inventoryCacheService.clear();
    this.inventoryCacheService.sync(true);
  }

  cacheStateLabel(): string {
    switch (this.cacheStatus && this.cacheStatus.state) {
      case 'loading': return 'Loading';
      case 'syncing': return 'Syncing';
      case 'ready': return 'Ready';
      case 'error': return 'Error';
      case 'empty': return 'Not loaded';
      default: return 'Off';
    }
  }

  ngOnInit() {
    this.activeTheme = this.userSettingsService.getTheme();
    this.subscriptions.push(this.userSettingsService.themeChangedObservable().subscribe(theme => {
      this.activeTheme = theme;
    }));

    this.showNumberpadOverride = this.deviceSettingsService.getShowNumberpadOverride();
    this.localCacheEnabled = this.deviceSettingsService.isLocalInventoryCacheEnabled();
    this.cacheStatus = this.inventoryCacheService.getStatus();
    this.queueStatus = this.transferQueueService.getStatus();
    this.subscriptions.push(this.inventoryCacheService.statusObservable().subscribe(status => { this.cacheStatus = status; }));
    this.subscriptions.push(this.transferQueueService.statusObservable().subscribe(status => { this.queueStatus = status; }));

    this.version = this.appUpdateService.version;
    this.updateReady = this.appUpdateService.isUpdateReady();
    if (this.updateReady) {
      this.updateStatus = 'updated';
    }
    this.subscriptions.push(this.appUpdateService.updateReadyObservable().subscribe(() => {
      this.updateReady = true;
      this.updateStatus = 'updated';
    }));

    this.user = this.authorizationService.getUser();
    this.refreshDeviceAuthState();
    this.subscriptions.push(this.deviceAuthService.deviceAuthChangedObservable().subscribe(() => {
      this.refreshDeviceAuthState();
    }));
    this.subscriptions.push(this.clientSettingsService.clientSettingsLoadedObservable().subscribe(() => {
      this.refreshDeviceAuthState();
    }));
    this.subscriptions.push(this.authorizationService.permissionsLoadedObservable().subscribe(() => {
      this.user = this.authorizationService.getUser();
      this.refreshDeviceAuthState();
    }));
  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  closeSettings() {
    this.settingsService.closeSettings();
  }

  setTheme(theme: ThemeKey) {
    this.userSettingsService.setTheme(theme);
  }

  setShowNumberpad(value: ShowNumberpadOverride) {
    this.showNumberpadOverride = value;
    this.deviceSettingsService.setShowNumberpadOverride(value);
  }

  clientNumberpadDefaultLabel(): string {
    return this.clientSettingsService.isShowNumberpadDefault() ? 'On' : 'Off';
  }

  // --- Device authorization ---------------------------------------------------

  private async refreshDeviceAuthState() {
    this.deviceAuth = this.deviceAuthService.isAuthorized() ? this.deviceAuthService.getDeviceAuth() : null;
    this.deviceSession = this.authenticationService.isDeviceSession();

    const settings = this.clientSettingsService.getDeviceAuthorizationSettings();
    this.deviceAuthEnabled = settings.enabled;
    this.deviceAuthMaxDays = settings.maxDays;
    if (!this.authorizeOpen) {
      this.authorizeDays = settings.maxDays;
    }

    // Only a password session may authorize a device (the server enforces
    // this too), and only at locations where the user holds the permission.
    this.canAuthorizeDevice = false;
    this.authorizeLocations = [];
    if (!this.deviceAuthEnabled || this.deviceSession || !this.authorizationService.permissionsAreLoaded()) {
      return;
    }

    let locations = this.locationService.getLocations();
    if (!locations) {
      try {
        await this.locationService.loadLocations();
      } catch (e) {
        return;
      }
      locations = this.locationService.getLocations() || [];
    }

    this.authorizeLocations = locations.filter(location =>
      location && location._id &&
      location.type !== 'vendor' && location.type !== 'adjustment' &&
      this.authorizationService.checkPermission(DEVICE_AUTHORIZE_PERMISSION, location._id));
    this.canAuthorizeDevice = this.authorizeLocations.length > 0;

    if (!this.authorizeLocationId || !this.authorizeLocations.find(location => location._id === this.authorizeLocationId)) {
      const active = this.locationService.getActiveLocation();
      const activeMatch = active && this.authorizeLocations.find(location => location._id === active._id);
      this.authorizeLocationId = (activeMatch || this.authorizeLocations[0] || {} as any)._id || null;
    }
  }

  deviceDaysRemaining(): number {
    return this.deviceAuthService.daysRemaining();
  }

  openAuthorize() {
    this.deviceError = null;
    this.authorizeOpen = true;
  }

  cancelAuthorize() {
    this.authorizeOpen = false;
    this.deviceError = null;
  }

  async authorizeDevice() {
    if (this.authorizing) {
      return;
    }
    this.deviceError = null;

    const name = (this.authorizeName || '').trim();
    if (!name) {
      this.deviceError = 'Give this device a name (for example "Warehouse Scanner 3").';
      return;
    }
    if (!this.authorizeLocationId) {
      this.deviceError = 'Choose a location.';
      return;
    }
    const days = Math.min(this.deviceAuthMaxDays, Math.max(1, Math.floor(Number(this.authorizeDays) || this.deviceAuthMaxDays)));

    const location = this.authorizeLocations.find(l => l._id === this.authorizeLocationId);
    const locationName = location ? location.name : 'this location';

    // The settings panel is a fixed overlay that outranks the dialog backdrop
    // on phone-width screens (handheld scanners), so close it before asking.
    // Closing destroys this component, so everything after the dialog reports
    // through the snackbar and reopens a fresh panel where needed.
    this.settingsService.closeSettings();
    const confirmRef = AppComponent.showMessage(
      'Authorize This Device',
      `Users at ${locationName} will be able to sign in on this device with their PIN for ${days} ` +
      `${days === 1 ? 'day' : 'days'}. Your session will end now so the device is left on the PIN screen.`,
      ['Confirm', 'Cancel'], false);
    const result: DialogOption = await confirmRef.afterClosed().toPromise();
    if (!result || result.value !== 'Confirm') {
      this.settingsService.launchSettings();
      return;
    }

    this.authorizing = true;
    try {
      await this.deviceAuthService.authorizeDevice(name, this.authorizeLocationId, days);
      // The manager's own session ends here; the sign-in screen comes up in
      // PIN mode with a confirmation notice.
      this.authenticationService.signout('authorized');
    } catch (e) {
      AppComponent.showSnack((e && e.error && e.error.msg) || 'Could not authorize this device.', 'OK', 6000);
      this.settingsService.launchSettings();
    } finally {
      this.authorizing = false;
    }
  }

  async removeDeviceAuthorization() {
    if (this.removingDevice) {
      return;
    }
    this.deviceError = null;

    // Same overlay-vs-dialog reasoning as authorizeDevice().
    this.settingsService.closeSettings();
    const confirmRef = AppComponent.showMessage(
      'Remove Device Authorization',
      'PIN sign-in will no longer work on this device until it is authorized again.' +
      (this.deviceSession ? ' Your current session will end.' : ''),
      ['Confirm', 'Cancel'], false);
    const result: DialogOption = await confirmRef.afterClosed().toPromise();
    if (!result || result.value !== 'Confirm') {
      this.settingsService.launchSettings();
      return;
    }

    this.removingDevice = true;
    try {
      await this.deviceAuthService.revokeThisDevice();
      if (this.deviceSession) {
        this.authenticationService.signout('deviceRevoked');
      } else {
        AppComponent.showSnack('Device authorization removed.', 'OK', 3000);
        this.settingsService.launchSettings();
      }
    } catch (e) {
      AppComponent.showSnack((e && e.error && e.error.msg) || 'Could not remove the device authorization.', 'OK', 6000);
      this.settingsService.launchSettings();
    } finally {
      this.removingDevice = false;
    }
  }

  checkForUpdates = async () => {
    if (this.updateStatus === 'checking') {
      return;
    }

    // Without a service worker (local development) the best we can do is a
    // plain reload, which re-fetches the app from the server.
    if (!this.appUpdateService.isSupported()) {
      document.location.reload();
      return;
    }

    this.updateStatus = 'checking';
    const result = await this.appUpdateService.checkForUpdates();
    switch (result) {
      case 'updated':
        this.updateReady = true;
        this.updateStatus = 'updated';
        break;
      case 'up-to-date':
        this.updateStatus = 'up-to-date';
        break;
      default:
        this.updateStatus = 'error';
    }
  }

  restartToUpdate() {
    this.appUpdateService.restartToUpdate();
  }
}
